import { Controller } from '@hotwired/stimulus'
import { getItem, setItem } from '../utils/local-storage'

const GEO_CACHE_TTL = 15 * 60 * 1000 // 15 минут
const SUGGESTIONS_DEBOUNCE = 250

/**
 * Base controller for Google Places-backed search inputs with optional browser
 * geolocation biasing.
 *
 * Uses Places API (New) via `AutocompleteSuggestion` with a custom dropdown
 * (the legacy `google.maps.places.Autocomplete` widget is unavailable for
 * Google Cloud projects created after March 2025).
 */
export default class GooglePlacesSearchController extends Controller {
  static targets = ['input', 'submit', 'latitude', 'longitude']
  // manageSubmit: false — кнопка сабмита всегда активна (пустой запрос
  // блокирует браузерная валидация required), true — кнопка задизейблена,
  // пока поле пустое.
  static values = { manageSubmit: { type: Boolean, default: true } }

  #placesInitialized = false
  #locationBias = null
  #geoRequested = false
  #onMapsReady = null
  #onInput = null
  #onKeydown = null
  #onBlur = null
  #onSubmit = null
  #debounceTimer = null
  #requestId = 0
  #dropdown = null
  #suggestions = []
  #activeIndex = -1
  #suppressSuggestions = false
  // Промис дозагрузки координат выбранной подсказки: сабмит ждёт его, иначе
  // быстрый клик по кнопке ушёл бы без координат.
  #coordinatesRequest = null

  get geoCacheKey() {
    throw new Error('Subclasses must implement geoCacheKey')
  }

  connect() {
    this.#onMapsReady = this.#initAutocomplete.bind(this)
    document.addEventListener('google-maps:ready', this.#onMapsReady)

    if (this.hasInputTarget && !this.#onInput) {
      this.#onInput = () => {
        // Ошибка «обязательное поле» показывается только по сабмиту —
        // как только пользователь редактирует поле, прячем её.
        this.#inputController()?.reset()
        // Правка строки руками отменяет координаты ранее выбранной подсказки:
        // иначе «Королёв» отправился бы с точкой от «Королёв, Бельгия».
        // Событие от #selectSuggestion сюда не считается — там координаты как
        // раз проставляются.
        if (!this.#suppressSuggestions) this.#clearCoordinates()
        this.#enableSubmit()
        if (this.#placesInitialized) this.#scheduleSuggestions()
      }
      this.inputTarget.addEventListener('input', this.#onInput)
    }

    // С пустым обязательным полем показываем стилизованную ошибку компонента
    // input вместо нативного тултипа (форме нужен novalidate).
    this.#onSubmit = (event) => this.#handleSubmit(event)
    this.element.addEventListener('submit', this.#onSubmit)

    if (window.google?.maps?.importLibrary) this.#initAutocomplete()
    if (this.hasInputTarget && this.inputTarget.value.trim() !== '')
      this.#enableSubmit()
  }

  disconnect() {
    document.removeEventListener('google-maps:ready', this.#onMapsReady)
    if (this.hasInputTarget) {
      if (this.#onInput)
        this.inputTarget.removeEventListener('input', this.#onInput)
      if (this.#onKeydown)
        this.inputTarget.removeEventListener('keydown', this.#onKeydown)
      if (this.#onBlur)
        this.inputTarget.removeEventListener('blur', this.#onBlur)
    }
    if (this.#onSubmit)
      this.element.removeEventListener('submit', this.#onSubmit)
    this.#onSubmit = null
    clearTimeout(this.#debounceTimer)
    this.#closeDropdown()
    this.#dropdown?.remove()
    this.#dropdown = null
    this.#coordinatesRequest = null
    this.#placesInitialized = false
    this.#locationBias = null
    this.#geoRequested = false
    this.#onInput = null
    this.#onKeydown = null
    this.#onBlur = null
  }

  geolocate() {
    if (!this.#capableOfGeolocation()) return

    const cached = this.#getGeoCache()
    if (cached) {
      this.#applyGeo(cached.lat, cached.lng, cached.accuracy)
      return
    }

    if (this.#geoRequested) return
    this.#geoRequested = true

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude: lat, longitude: lng, accuracy } }) => {
        this.#setGeoCache(lat, lng, accuracy)
        this.#applyGeo(lat, lng, accuracy)
        this.#geoRequested = false
      },
      () => {
        this.#geoRequested = false
      }
    )
  }

  selectLocation(event) {
    const location = event.target.dataset.location
    if (location && this.hasInputTarget) {
      this.inputTarget.value = location
      this.inputTarget.focus()
      this.#enableSubmit()
    }
  }

  #initAutocomplete() {
    if (!this.hasInputTarget || this.#placesInitialized) return
    this.#placesInitialized = true

    this.inputTarget.setAttribute('autocomplete', 'off')
    this.inputTarget.setAttribute('role', 'combobox')
    this.inputTarget.setAttribute('aria-expanded', 'false')

    this.#onKeydown = (event) => this.#handleKeydown(event)
    this.inputTarget.addEventListener('keydown', this.#onKeydown)
    this.#onBlur = () => setTimeout(() => this.#closeDropdown(), 150)
    this.inputTarget.addEventListener('blur', this.#onBlur)
  }

  #scheduleSuggestions() {
    if (this.#suppressSuggestions) return
    clearTimeout(this.#debounceTimer)
    const query = this.inputTarget.value.trim()
    if (query === '') {
      this.#closeDropdown()
      return
    }
    this.#debounceTimer = setTimeout(
      () => this.#fetchSuggestions(query),
      SUGGESTIONS_DEBOUNCE
    )
  }

  async #fetchSuggestions(query) {
    if (!window.google?.maps?.importLibrary) return

    const requestId = ++this.#requestId

    try {
      const { AutocompleteSuggestion } =
        await google.maps.importLibrary('places')

      const request = {
        input: query,
        includedPrimaryTypes: ['(regions)']
      }
      const language = document.documentElement.lang
      if (language) request.language = language
      if (this.#locationBias) request.locationBias = this.#locationBias

      const { suggestions } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      if (requestId !== this.#requestId) return // устаревший ответ

      // Places API (New) разделяет части адреса " - ", а серверный
      // LocationSearchService парсит запросы по запятым (формат legacy-виджета)
      //
      // placeId сохраняем рядом с текстом: по нему при выборе подсказки
      // дотягиваются координаты локации. Города, которого нет в нашей БД,
      // на карте для сервера иначе не существует.
      this.#suggestions = suggestions
        .map((suggestion) => suggestion.placePrediction)
        .filter((prediction) => prediction?.text?.text)
        .map((prediction) => ({
          text: prediction.text.text.replaceAll(' - ', ', '),
          placeId: prediction.placeId
        }))
      this.#renderDropdown()
    } catch {
      this.#closeDropdown()
    }
  }

  #renderDropdown() {
    if (this.#suggestions.length === 0) {
      this.#closeDropdown()
      return
    }

    const dropdown = this.#ensureDropdown()
    dropdown.innerHTML = ''
    this.#activeIndex = -1

    this.#suggestions.forEach(({ text }, index) => {
      const item = document.createElement('div')
      item.className = 'pac-item'
      item.setAttribute('role', 'option')
      item.setAttribute('aria-selected', 'false')
      item.textContent = text
      // mousedown вместо click — иначе blur инпута закроет список раньше
      item.addEventListener('mousedown', (event) => {
        event.preventDefault()
        this.#selectSuggestion(index)
      })
      dropdown.appendChild(item)
    })

    dropdown.style.display = 'flex'
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  #ensureDropdown() {
    if (this.#dropdown) return this.#dropdown

    const container =
      this.inputTarget.closest('.input') || this.inputTarget.parentElement
    if (getComputedStyle(container).position === 'static')
      container.style.position = 'relative'

    const dropdown = document.createElement('div')
    dropdown.className = 'pac-container'
    dropdown.setAttribute('role', 'listbox')
    dropdown.style.position = 'absolute'
    dropdown.style.top = '100%'
    dropdown.style.left = '0'
    dropdown.style.right = '0'
    dropdown.style.zIndex = '1000'
    dropdown.style.display = 'none'
    container.appendChild(dropdown)

    this.#dropdown = dropdown
    return dropdown
  }

  #closeDropdown() {
    this.#suggestions = []
    this.#activeIndex = -1
    if (this.#dropdown) {
      this.#dropdown.style.display = 'none'
      this.#dropdown.innerHTML = ''
    }
    if (this.hasInputTarget)
      this.inputTarget.setAttribute('aria-expanded', 'false')
  }

  #handleKeydown(event) {
    if (this.#suggestions.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.#moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.#moveActive(-1)
        break
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault()
          this.#selectSuggestion(this.#activeIndex)
        }
        break
      case 'Escape':
        this.#closeDropdown()
        break
    }
  }

  #moveActive(delta) {
    const count = this.#suggestions.length
    this.#activeIndex = (this.#activeIndex + delta + count) % count

    const items = this.#dropdown?.querySelectorAll('.pac-item') || []
    items.forEach((item, index) => {
      item.classList.toggle('pac-item--active', index === this.#activeIndex)
      item.setAttribute(
        'aria-selected',
        index === this.#activeIndex ? 'true' : 'false'
      )
    })
  }

  #selectSuggestion(index) {
    const suggestion = this.#suggestions[index]
    if (!suggestion) return

    const { text, placeId } = suggestion

    clearTimeout(this.#debounceTimer) // отменяет отложенный #fetchSuggestions
    this.#debounceTimer = null
    this.inputTarget.value = text
    this.#requestId++ // отменяет запросы в полете
    this.#closeDropdown()
    this.inputTarget.focus()
    this.#suppressSuggestions = true
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.#suppressSuggestions = false
    this.#enableSubmit()
    this.#loadCoordinates(placeId)
  }

  /**
   * Кладёт координаты выбранной локации в скрытые поля формы.
   *
   * Нужны серверу, чтобы показать ближайшие клубы для города, которого нет в
   * нашей БД. Форма без полей latitude/longitude (поиск тренеров) ничего не
   * запрашивает: лишний вызов Places стоит денег.
   *
   * @param {string|undefined} placeId - Идентификатор места из подсказки.
   * @returns {void}
   */
  #loadCoordinates(placeId) {
    if (!this.hasLatitudeTarget || !this.hasLongitudeTarget) return
    if (!placeId || !window.google?.maps?.importLibrary) return

    this.#coordinatesRequest = (async () => {
      try {
        const { Place } = await google.maps.importLibrary('places')
        const place = new Place({ id: placeId })
        await place.fetchFields({ fields: ['location'] })

        const location = place.location
        if (!location) return

        this.latitudeTarget.value = location.lat()
        this.longitudeTarget.value = location.lng()
      } catch {
        this.#clearCoordinates()
      }
    })()
  }

  #clearCoordinates() {
    this.#coordinatesRequest = null
    if (this.hasLatitudeTarget) this.latitudeTarget.value = ''
    if (this.hasLongitudeTarget) this.longitudeTarget.value = ''
  }

  #applyGeo(lat, lng, accuracy) {
    this.#locationBias = { center: { lat, lng }, radius: accuracy }
  }

  #handleSubmit(event) {
    if (!this.hasInputTarget) return

    const input = this.inputTarget
    if (input.required && input.value.trim() === '') {
      event.preventDefault()
      this.#inputController()?.validate()
      input.focus()
      return
    }

    // Координаты подсказки могли ещё не прийти: между кликом по подсказке и
    // кликом по кнопке проходят доли секунды, а fetchFields — сетевой запрос.
    // Отправка без них не сломается, но выдача потеряет ближайшие клубы,
    // поэтому ждём и отправляем форму повторно.
    if (!this.#coordinatesRequest) return

    const pending = this.#coordinatesRequest
    event.preventDefault()
    pending.finally(() => {
      // Свежий выбор подсказки успел заменить запрос — его и ждёт свой сабмит.
      if (this.#coordinatesRequest !== pending) return

      this.#coordinatesRequest = null
      this.element.requestSubmit()
    })
  }

  #inputController() {
    if (!this.hasInputTarget) return null

    const element = this.inputTarget.closest('[data-controller~="input"]')
    if (!element) return null

    return this.application.getControllerForElementAndIdentifier(
      element,
      'input'
    )
  }

  #enableSubmit() {
    if (!this.manageSubmitValue) return

    const button = this.hasSubmitTarget
      ? this.submitTarget
      : this.element.querySelector('[type=submit]')
    if (button && this.hasInputTarget)
      button.disabled = this.inputTarget.value.trim() === ''
  }

  #capableOfGeolocation() {
    return 'geolocation' in navigator && window.isSecureContext
  }

  #getGeoCache() {
    const key = this.geoCacheKey

    try {
      const cached = JSON.parse(getItem(key))
      if (cached && Date.now() - cached.timestamp < GEO_CACHE_TTL) return cached
    } catch {
      console.error('Failed to access geolocation cache')
    }
    return null
  }

  #setGeoCache(lat, lng, accuracy) {
    const key = this.geoCacheKey

    try {
      setItem(
        key,
        JSON.stringify({ lat, lng, accuracy, timestamp: Date.now() })
      )
    } catch {
      console.error('Failed to set geolocation cache')
    }
  }
}
